<template>
  <v-row class="ma-0 pa-0">
    <v-col class="pa-0">
      <div>
        <v-row class="ma-0 pa-0">
          <!-- clusters -->
          <v-col
            class="pa-0 panel-col right-border"
            cols="4"
            style="min-width: 315px; align-items: stretch"
          >
            <v-card color="#eeeeee" class="pt-1 title-card" style="">
              <v-card-title class="primary--text pl-3 py-2"
                >Show/Create Groups
                <v-spacer />
                <v-chip
                  v-if="showFilterByClusterChip"
                  label
                  close
                  color="#4caf50"
                  class="font-weight-bold white--text pa-2 my-2"
                  @click:close="showFilterByClusterChip = false"
                >
                  Option 2: Filter by Groups
                </v-chip>
              </v-card-title>
            </v-card>
            <div style=" padding-left: 1rem; padding-right:1rem">
              <div style="display: flex;">
                <v-simple-checkbox
                  v-model="draw_clusters"
                  hide-details
                  color="primary"
                  class="shrink"
                />
                <span>Show groups</span>
                <v-tooltip v-if="draw_clusters" top color="primary">
                  <template v-slot:activator="{ on: tooltip }">
                    <span v-on="{ ...tooltip }">
                      <v-badge
                        color="primary"
                        :content="num_clusters_displayed + '/' + num_clusters"
                        class="pl-3"
                      ></v-badge>
                    </span>
                  </template>
                  <span
                    >Displaying {{ num_clusters_displayed }} of
                    {{ num_clusters }} groups</span
                  >
                </v-tooltip>
              </div>

              <v-row class="pa-0 ma-0">
                <v-chip
                  v-if="showLogicChip"
                  label
                  close
                  color="#4caf50"
                  class="font-weight-bold white--text pa-2 my-1"
                  @click:close="showLogicChip = false"
                >
                  Change logic options to change how groups are created
                </v-chip>
              </v-row>

              <v-row class="pa-0 mb-2 mt-n1">
                <!-- clusters and step size -->
                <v-col cols="6" class="mt-1">
                  <v-card>
                    <v-card-title class="text-body-1"
                      >Logic Options</v-card-title
                    >
                    <v-card-text>
                      <!-- Last Visit or Change dropdown -->
                      <v-combobox
                        v-model="cluster_choice"
                        :disabled="!draw_clusters"
                        :items="cluster_choices"
                        label="Group by"
                        dense
                      ></v-combobox>

                      <!-- step value (0.5, 1, 1.5) -->
                      <div>
                        <v-combobox
                          v-model="cluster_step_choice"
                          style="margin-top: 1rem;"
                          :disabled="!draw_clusters"
                          :items="cluster_step_choices"
                          label="Group step size"
                          dense
                        ></v-combobox>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- display style and min cluster size  -->
                <v-col cols="6" class="mt-1">
                  <v-card style="height:100%">
                    <v-card-title class="text-body-1">
                      Visual Options
                    </v-card-title>

                    <v-card-text>
                      <!-- Cluster display style -->
                      <v-combobox
                        v-model="cluster_style_choice"
                        :disabled="!draw_clusters"
                        :items="cluster_style_choices"
                        label="Group display style"
                        dense
                      ></v-combobox>

                      <!-- min cluster slider -->
                      <div style="display:flex; margin-top: -1rem;">
                        <v-subheader class="pl-0" style="padding-right:10px">
                          Min. group size:
                        </v-subheader>
                        <div style="width: 40px;">
                          <v-text-field
                            v-model="min_cluster_size"
                            style="margin-top: 0.6rem;"
                            hide-details
                            dense
                            :disabled="!draw_clusters"
                            solo-filled
                          ></v-text-field>
                        </div>
                      </div>
                      <v-slider
                        v-model="min_cluster_size"
                        style="margin-top: -0.5rem;"
                        :disabled="!draw_clusters"
                        thumb-size="24"
                        :min="1"
                        :max="unfilteredData.length"
                        hide-details
                        dense
                      >
                      </v-slider>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-row class="pa-0 ma-0">
                <v-chip
                  v-if="showClusterChip"
                  label
                  close
                  color="#4caf50"
                  class="font-weight-bold white--text pa-2"
                  @click:close="showClusterChip = false"
                >
                  Click on group to filter OR use checkbox to save selected
                </v-chip>
              </v-row>

              <v-divider class="mt-2 mb-2"></v-divider>
              <v-simple-table class="pa-0 ma-0">
                <thead>
                  <tr>
                    <th>
                      <v-col class="ma-0 pa-0">
                        <span class="text-subtitle-2 font-weight-bold">
                          Selected:
                        </span>
                        <v-tooltip top color="primary">
                          <template v-slot:activator="{ on: tooltip }">
                            <v-simple-checkbox
                              v-model="allClustersSelected"
                              class="ma-0 pa-0 ml-4"
                              color="primary"
                              v-on="{ ...tooltip }"
                              @click="selectAllClusters"
                            ></v-simple-checkbox>
                          </template>
                          <span>Click to select/unselect all groups</span>
                        </v-tooltip>
                      </v-col>
                    </th>
                    <th>
                      <span class="text-subtitle-2 font-weight-bold">
                        Group (SD)
                      </span>
                    </th>
                    <th>
                      <span class="text-subtitle-2 font-weight-bold">
                        Size
                      </span>
                    </th>
                    <th>
                      <span class="text-subtitle-2 font-weight-bold">
                        Visible
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- when click on any row, highlight that row  -->
                  <tr
                    v-for="(c, index) in clusters"
                    :key="`cl-${index}`"
                    :class="{ 'highlighted-row': clickedCluster === c }"
                    :style="
                      c.data.length > 0
                        ? { cursor: 'pointer' }
                        : { backgroundColor: 'transparent' }
                    "
                    @mouseover="clusterHover(c)"
                    @mouseleave="clusterUnhover(c)"
                    @click="clusterClick(c)"
                  >
                    <td>
                      <v-simple-checkbox
                        v-model="c.selected"
                        class="ma-0 pa-0 ml-4"
                        color="primary"
                        :disabled="!draw_clusters || c.data.length == 0"
                      ></v-simple-checkbox>
                    </td>
                    <td :style="c.data.length == 0 ? { color: 'gray' } : {}">
                      {{ c.label }}
                    </td>
                    <td :style="c.data.length == 0 ? { color: 'gray' } : {}">
                      {{ c.data.length }}
                    </td>
                    <td justify="center">
                      <v-icon v-if="c.displayed">done</v-icon>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>

            <v-row class="ma-0 pa-0 my-2" justify="center">
              <!-- "SAVE SIGNLE CLUSTER" button -->
              <v-tooltip top color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <v-btn
                    :disabled="!draw_clusters || numSelectedClusters == 0"
                    color="primary"
                    class="ma-2"
                    style="margin-top: 1rem;"
                    v-on="{ ...tooltip }"
                    @click="saveClusters"
                  >
                    Save
                  </v-btn>
                </template>
                <span>Save selected groups as separate study groups</span>
              </v-tooltip>

              <v-tooltip top color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <v-btn
                    :disabled="!draw_clusters || numSelectedClusters < 2"
                    color="primary"
                    class="ma-2"
                    style="margin-top: 1rem;"
                    v-on="{ ...tooltip }"
                    @click="mergeAndSaveClusters"
                  >
                    Merge & Save
                  </v-btn>
                </template>
                <span
                  >Combine adjacent selected groups and save as a single study
                  group</span
                >
              </v-tooltip>
            </v-row>
          </v-col>

          <!-- spaghetti plot & manual mode -->
          <v-col
            class="pa-0 panel-col right-border"
            cols="4"
            style="min-width: 385px;"
          >
            <v-card color="#eeeeee" class="pt-1 title-card">
              <v-card-title class="primary--text pl-3 py-2"
                >First & Last Visit
                <v-spacer />
                <v-chip
                  v-if="showFilterByLastVisitChip"
                  label
                  close
                  color="#4caf50"
                  class="font-weight-bold white--text pa-2 my-2"
                  @click:close="showFilterByLastVisitChip = false"
                >
                  Option 1: Filter by Last Visit
                </v-chip>
              </v-card-title>
            </v-card>

            <!-- Expansion panels -->
            <!-- if you would like this part to fill the rest of the space, add flex-grow: 1;-->
            <v-expansion-panels
              accordion
              value="0"
              style="margin-top: -13px; margin-bottom: 15px;"
            >
              <!-- Show Individual subject -->
              <v-expansion-panel value="0">
                <v-expansion-panel-header>
                  <v-simple-checkbox
                    v-model="show_raw_data"
                    hide-details
                    color="primary"
                    class="shrink"
                  />Show individual subjects
                  <v-tooltip
                    v-if="show_raw_data && subsample_data"
                    top
                    color="primary"
                  >
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-badge
                          color="primary"
                          :content="subsample_labels[subsample_choice]"
                          class="pl-3"
                        ></v-badge>
                      </span>
                    </template>
                    <span
                      >Subsampling enabled: only
                      {{ subsample_labels[subsample_choice] }} of subjects are
                      shown</span
                    >
                  </v-tooltip>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pl-3">
                  <v-checkbox
                    v-model="subsample_data"
                    :disabled="!show_raw_data"
                    hide-details
                    dense
                    label="Subsample:"
                    class="pa-0 ma-0"
                  />
                  <v-slider
                    v-model="subsample_choice"
                    :disabled="!subsample_data || !show_raw_data"
                    step="1"
                    max="4"
                    ticks="always"
                    :tick-labels="subsample_labels"
                  ></v-slider>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <div
              fluid
              fill-height
              class="ma-0 pa-0"
              style="max-width: 425px;margin:1rem"
            >
              <v-row class="ma-0 pa-0">
                <v-col cols="12" class="ma-0 pa-0">
                  <v-card-title>
                    <span class="subtitle-1 text-wrap">
                      <b>Visit:</b> {{ firstVisitLabel }}
                    </span>

                    <v-spacer></v-spacer>

                    <v-btn
                      class="primary text--white ma-0 px-2 py-0"
                      @click="resetClicked('Last Visit')"
                    >
                      Reset
                    </v-btn>

                    <v-spacer></v-spacer>

                    <span class="subtitle-1 text-wrap">
                      <b>Visit:</b>&nbsp;{{ lastVisitLabel }}
                    </span>
                  </v-card-title>
                </v-col>
              </v-row>
              <v-row class="ma-0 pa-0">
                <v-chip
                  v-if="showHelpChip"
                  label
                  close
                  color="#4caf50"
                  class="font-weight-bold white--text pa-2 my-2"
                  @click:close="showHelpChip = false"
                >
                  Click and drag to filter OR use ranges.
                </v-chip>
              </v-row>
              <v-row class="ma-0 pa-0">
                <v-col cols="12" class="ma-0 pa-0">
                  <!-- First Visit histogram -->
                  <VerticalHistogram
                    :id="`firstVisit-${dimensionName}`"
                    left
                    :dimension-name="`${variable.label} - First Visit`"
                    :y-min="minValueBetweenDimensions"
                    :y-domain="maxValueBetweenDimensions"
                    :n-yvals="numDistinctValues"
                    :variable="variable.children[0]"
                    :interactive="false"
                    @userChangedVariable="userChangedVariable"
                  />
                  <!-- Parallel Coordinates -->
                  <canvas
                    v-if="true"
                    ref="canvas"
                    :width="computedWidth"
                    :height="computedHeight"
                    :style="'padding: 0px; margin: 0px;'"
                  >
                  </canvas>
                  <!-- Parallel Coordinates - SVG -->
                  <svg
                    v-if="false"
                    ref="pcoords"
                    :width="computedWidth"
                    :height="computedHeight"
                    :style="'padding: 0px; margin: 0px;'"
                  ></svg>
                  <!-- Last visit histogram -->
                  <VerticalHistogram
                    :id="`lastVisit-${dimensionName}`"
                    ref="last"
                    :manual-min="tfRangeMin"
                    :manual-max="tfRangeMax"
                    :dimension-name="`${variable.label} - Last Visit`"
                    :y-min="minValueBetweenDimensions"
                    :y-domain="maxValueBetweenDimensions"
                    :n-yvals="numDistinctValues"
                    :variable="variable.children[1]"
                    :predef_radio="predef_radio"
                    :padded_min_value="padded_min_value"
                    :padded_max_value="padded_max_value"
                    @userChangedVariable="userChangedVariable"
                    @userChangedFilter="userChangedFilter"
                    @updateRangeFromQuery="updateRangeFromQuery"
                  />
                </v-col>
              </v-row>
            </div>

            <v-tabs v-model="tab" style="flex-grow: 0;">
              <v-tab key="manual">Manual</v-tab>
              <v-tab key="predef">Pre-defined Ranges</v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab">
              <v-tab-item key="manual">
                <v-container fill-width class="pl-3 pa-0 mx-3 mt-2">
                  <v-row class="pa-0 ma-0">
                    <v-col
                      cols="5"
                      class="center-text pa-0 ma-0 font-weight-bold"
                      justify="start"
                      >Selected&nbsp;Range:</v-col
                    >
                    <v-col cols="2" class="pa-0 ma-0" justify="start">
                      <v-text-field
                        v-model.number="tfRangeMin"
                        :error-messages="rangeMinErrors"
                        class="center-text pa-0 ma-0"
                        type="number"
                        @change="userChangedVariable"
                      ></v-text-field
                    ></v-col>
                    <v-col
                      cols="1"
                      class="pa-0 ma-0"
                      align="center"
                      justify="center"
                    >
                      -
                    </v-col>
                    <v-col cols="2" class="pa-0 ma-0">
                      <v-text-field
                        v-model.number="tfRangeMax"
                        :error-messages="rangeMaxErrors"
                        class="center-text pa-0 ma-0"
                        type="number"
                        @change="userChangedVariable"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-tab-item>

              <v-tab-item key="predef" style="padding-bottom: 15px;">
                <v-container fill-width class="pa-0 mx-3 mt-2">
                  <v-row class="pa-0 ma-0">
                    <v-col cols="12" class="pa-0 ma-0">
                      <v-radio-group
                        v-model="predef_radio"
                        hide-details
                        row
                        class="pa-2 my-1"
                      >
                        <v-radio value="halves" label="Halves"></v-radio>
                        <v-radio value="tertiles" label="Tertiles"></v-radio>
                        <v-radio value="quartiles" label="Quartiles"></v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-row>

                  <v-row class="pa-0 ma-0">
                    <v-col
                      cols="5"
                      class="center-text pa-0 ma-0 font-weight-bold"
                      justify="start"
                    >
                      <v-select
                        v-model="selectedPopSubset"
                        :items="popSubsetItems"
                        item-text="label"
                        item-value="id"
                        label="select range"
                        class="pa-0 ma-0 mt-1"
                        return-object
                      ></v-select>
                    </v-col>
                    <v-col cols="2" class="pa-0 ma-0 ml-2" justify="start">
                      <v-text-field
                        v-model.number="tfRangeMin"
                        :error-messages="rangeMinErrors"
                        class="center-text pa-0 ma-0"
                        type="number"
                        @change="userChangedVariable"
                      ></v-text-field
                    ></v-col>
                    <v-col
                      cols="1"
                      class="pa-0 ma-0"
                      align="center"
                      justify="center"
                    >
                      -
                    </v-col>
                    <v-col cols="2" class="pa-0 ma-0">
                      <v-text-field
                        v-model.number="tfRangeMax"
                        :error-messages="rangeMaxErrors"
                        class="center-text pa-0 ma-0"
                        type="number"
                        @change="userChangedVariable"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row class="pa-0 ma-0 mb-1">
                    <v-col cols="12" class="pa-0 ma-0 pr-4" align="start">
                      <v-btn
                        class="primary text--white ma-0 px-2 mx-1 py-0"
                        @click="comparePredefs"
                        >Compare {{ predef_radio }}</v-btn
                      >

                      <v-btn
                        class="primary text--white ma-0 px-2 mx-1 py-0"
                        @click="savePredefs"
                        >Save {{ predef_radio }}</v-btn
                      >
                    </v-col>
                  </v-row>
                </v-container>
              </v-tab-item>
            </v-tabs-items>
          </v-col>

          <!-- Change & Rate of Change-->
          <v-col class="pa-0 panel-col" style="min-width: 330px;">
            <v-card color="#eeeeee" class="pt-1 title-card" style="">
              <v-card-title class="primary--text pl-3 py-2"
                >Change & Rate of Change
                <v-spacer />
                <v-chip
                  v-if="showFilterByChangeChip"
                  label
                  close
                  color="#4caf50"
                  class="font-weight-bold white--text pa-2 my-2"
                  @click:close="showFilterByChangeChip = false"
                >
                  Option 3: Filter by Change
                </v-chip>
              </v-card-title>
            </v-card>

            <v-col cols="12" class="ma-0 pa-0">
              <!-- Change Histogram -->
              <v-card-title class="ma-0 pa-0 mx-4">
                <!-- Title for Change Histogram -->
                <span class="subtitle-1 text-wrap">
                  <b>Change:</b> {{ firstVisitLabel }} - {{ lastVisitLabel }}
                </span>

                <v-spacer></v-spacer>

                <!-- reset button -->
                <v-btn
                  class="primary text--white ma-0 px-2 py-0"
                  @click="resetClicked('Change')"
                >
                  Reset
                </v-btn>
              </v-card-title>

              <!-- Actual graph -->
              <HistogramChart
                :id="`change-${dimensionName}`"
                :dimension-name="`${variable.label} - Change`"
                :variable="variable.children[2]"
                :show-filter-help="showFilterHelp"
                input-variable
                :show-best-and-worst="
                  variable.children[2].type == 'observation' &&
                    (!variable.children[2].is_longitudinal ||
                      (variable.children[2].label == 'First Visit' ||
                        variable.children[2].label == 'Last Visit'))
                "
                @savePredefinedRanges="savePredefinedRanges"
                @comparePredefinedRanges="comparePredefinedRanges"
                @userChangedVariable="userChangedVariable"
              />

              <!-- Rate of Change Histogram -->
              <v-card-title class="ma-0 pa-0 mx-4">
                <!-- Title for ROC Histogram -->
                <span class="subtitle-1 text-wrap">
                  <b>Rate of Change:</b> {{ firstVisitLabel }} -
                  {{ lastVisitLabel }}
                </span>

                <v-spacer></v-spacer>

                <!-- reset button -->
                <v-btn
                  class="primary text--white ma-0 px-2 py-0"
                  @click="resetClicked('Rate of Change')"
                >
                  Reset
                </v-btn>
              </v-card-title>

              <!-- Actual graph -->
              <HistogramChart
                :id="`roc-${dimensionName}`"
                :dimension-name="`${variable.label} - Rate of Change`"
                :variable="variable.children[3]"
                :show-filter-help="showFilterHelp"
                input-variable
                :show-best-and-worst="
                  variable.children[3].type == 'observation' &&
                    (!variable.children[3].is_longitudinal ||
                      (variable.children[3].label == 'First Visit' ||
                        variable.children[3].label == 'Last Visit'))
                "
                @savePredefinedRanges="savePredefinedRanges"
                @comparePredefinedRanges="comparePredefinedRanges"
                @userChangedVariable="userChangedVariable"
              />
            </v-col>
          </v-col>
        </v-row>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { state, actions } from '@/store/modules/cohortManager/types';
import { actions as notifyActions } from '@/store/modules/notifications/types';
import { scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { extent, min, max, mean, deviation } from 'd3-array';
import { colors, hexToRgbA } from '@/utils/colors';
import VerticalHistogram from '@/components/CohortManager/VerticalHistogram/VerticalHistogram.vue';
import HistogramChart from '@/components/CohortManager/HistogramChart/HistogramChart.vue';

export default {
  directives: {
    dimension(el, binding) {
      const axisMethod = binding.value;
      select(el)
        .transition()
        .call(axisMethod);
    },
  },
  components: {
    VerticalHistogram,
    HistogramChart,
  },
  props: {
    dimensionName: {
      type: Number,
      required: true,
    },
    variable: {
      type: Object,
      required: true,
    },
    firstVisitLabel: {
      type: String,
      required: false,
      default: 'First Visit',
    },
    lastVisitLabel: {
      type: String,
      required: false,
      default: 'Last Visit',
    },
    showFilterHelp: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      tfRangeMin: null,
      tfRangeMax: null,
      width: 0,
      height: 0,
      margin: { top: 20, right: 0, bottom: 10, left: 0 },
      mounted: false,
      show_raw_data: false,
      subsample_data: true,
      draw_clusters: true,
      subsample_labels: ['1%', '5%', '10%', '20%', '50%'],
      subsample_choice: 2,
      min_cluster_size: 10,
      cluster_choice: 'Last Visit',
      cluster_choices: ['Change', 'Last Visit'],
      cluster_style_choice: 'Average +/- 1 SD',
      cluster_style_choices: [
        'Average (width=n_subjects)',
        'Average +/- 1 SD',
        'Average + all subjects',
      ],
      cluster_step_choice: '1.5 SD',
      cluster_step_choices: ['0.25 SD', '0.5 SD', '1 SD', '1.5 SD'],
      num_clusters: 0,
      num_clusters_displayed: 0,
      clusters: [],
      clusters_cache: {},
      clickedCluster: null,
      clusterThreshold: [],
      clusterErrorMessage: '',
      tab: 'manual',
      predef_radio: 'halves',
      selectedPopSubset: null,
      dimension: null,
      subsamplingMin: 20000,
      showHelpChip: false,
      showLogicChip: false,
      showClusterChip: false,
      showFilterByLastVisitChip: false,
      showFilterByClusterChip: false,
      showFilterByChangeChip: false,
      allClustersSelected: false,
    };
  },
  computed: {
    ...mapState('cohortManager', {
      filteredData: state.FILTERED_DATA,
      unfilteredData: state.UNFILTERED_DATA,
      dimensions: state.DIMENSIONS,
      inputVariables: state.INPUT_VARIABLES,
      outputVariables: state.OUTPUT_VARIABLES,
      collection: state.COLLECTION,
      helpMode: state.HELP_MODE,
      cohort: state.COHORT,
      cohorts: state.COHORTS,
    }),
    category() {
      // if this.varaible.category exists, use it
      // if not use this.variable.parent.label
      let cat = this.variable.category
        ? this.variable.category
        : this.variable.parent.label;

      return cat;
    },
    rangeMinErrors() {
      if (this.tfRangeMin) {
        if (this.tfRangeMin < this.padded_min_value) {
          return ['Minimum is ' + this.padded_min_value];
        }
        if (this.tfRangeMin > this.padded_max_value) {
          return ['Maximum is ' + this.padded_max_value];
        }
        if (this.tfRangeMax && this.tfRangeMin > this.tfRangeMax) {
          return ['Minimum is greater than maximum'];
        }
      }
      return [];
    },
    min_value() {
      var ext = extent(this.populationData);
      return ext[0];
    },
    padded_min_value() {
      return this.minValueBetweenDimensions;
    },
    max_value() {
      var ext = extent(this.populationData);
      return ext[1];
    },
    // padded maximum value
    padded_max_value() {
      return this.maxValueBetweenDimensions;
    },
    rangeMaxErrors() {
      if (this.tfRangeMax) {
        if (this.tfRangeMax < this.padded_min_value) {
          return ['Minimum is ' + this.padded_min_value];
        }
        if (this.tfRangeMax > this.padded_max_value) {
          return ['Maximum is ' + this.padded_max_value];
        }
        if (this.tfRangeMin && this.tfRangeMin > this.tfRangeMax) {
          return ['Maximum is less than minimum'];
        }
      }
      return [];
    },
    populationData() {
      return this.unfilteredData.map(d => {
        let dv = d[this.dimensionName];
        return { subject_id: d['subject_id'], ...dv };
      });
    },
    cohortData() {
      return this.filteredData.map(d => {
        let dv = d[this.dimensionName];
        return { subject_id: d['subject_id'], ...dv };
      });
    },
    subsampledPopulationData() {
      return this.subsample(this.unfilteredData).map(d => {
        let dv = d[this.dimensionName];
        return { subject_id: d['subject_id'], ...dv };
      });
    },
    populationPaths() {
      const fvx = this.xDimensionScale('First Visit');
      const lvx = this.xDimensionScale('Last Visit');
      const dscale = this.dimensionScale;
      const data = this.subsample_data
        ? this.subsampledPopulationData
        : this.populationData;

      return data.map(d => {
        const subject_id = d.subject_id;
        const firstVisitCoordinates = {
          x: fvx,
          y: dscale(d.firstVisit),
        };
        const lastVisitCoordinates = {
          x: lvx,
          y: dscale(d.lastVisit),
        };
        return {
          subject_id,
          firstVisitCoordinates,
          lastVisitCoordinates,
        };
      });
    },
    numDistinctValues() {
      let dv = {};
      this.populationData.forEach(d => {
        dv[d.firstVisit] = 1;
        dv[d.lastVisit] = 1;
      });
      return Object.keys(dv).length;
    },
    minValueBetweenDimensions() {
      const firstMin = min(this.populationData, d => d.firstVisit);
      const lastMin = min(this.populationData, d => d.lastVisit);
      return Math.min(firstMin, lastMin);
    },
    maxValueBetweenDimensions() {
      const firstMax = max(this.populationData, d => d.firstVisit);
      const lastMax = max(this.populationData, d => d.lastVisit);
      return Math.max(firstMax, lastMax);
    },
    computedWidth() {
      const { left, right } = this.margin;
      const { width } = this;
      return width - left - right;
    },
    computedHeight() {
      const { height } = this;
      return height;
    },
    dimensionScale() {
      const scale = scaleLinear()
        .domain(
          this.variable.flip_axis
            ? [this.minValueBetweenDimensions, this.maxValueBetweenDimensions]
            : [this.maxValueBetweenDimensions, this.minValueBetweenDimensions]
        )
        .range([this.margin.top, this.computedHeight - this.margin.bottom]);
      return scale;
    },
    popSubsetItems() {
      if (!this.mounted) {
        return;
      }
      return this.$refs.last.popSubsetItems();
    },
    numSelectedClusters() {
      var count = 0;
      this.clusters.forEach(c => {
        if (c.selected) {
          count = count + 1;
        }
      });
      return count;
    },
  },
  watch: {
    selectedPopSubset(s) {
      this.$refs.last.selectedPopSubset = s;
    },
    tfRangeMin() {
      if (this.tfRangeMin == '') {
        this.tfRangeMin = null;
      }
    },
    tfRangeMax() {
      if (this.tfRangeMax == '') {
        this.tfRangeMax = null;
      }
    },
    cohortData() {
      this.$nextTick(() => this.updateCanvas());
    },
    subsample_choice() {
      this.$nextTick(() => this.updateCanvas());
    },
    cluster_choice() {
      this.getClusters();
      this.clickedCluster = null;
      this.clearAllFilters();
      this.$nextTick(() => this.updateCanvas());
    },
    cluster_step_choice() {
      this.getClusters();
      this.clickedCluster = null;
      this.clearAllFilters();
      this.$nextTick(() => this.updateCanvas());
    },
    cluster_style_choice() {
      this.$nextTick(() => this.updateCanvas());
    },
    show_raw_data() {
      this.$nextTick(() => this.updateCanvas());
    },
    subsample_data() {
      this.$nextTick(() => this.updateCanvas());
    },
    draw_clusters() {
      this.$nextTick(() => this.updateCanvas());
    },
    min_cluster_size() {
      this.$nextTick(() => this.updateCanvas());
    },
    cohort() {
      if (this.cohort.id != -1) {
        this.clickedCluster = null;
        this.$nextTick(() => this.updateCanvas());
      }
    },
    showFilterHelp(show) {
      this.showHelpChip = show;
      this.showLogicChip = show;
      this.showClusterChip = show;
      this.showFilterByLastVisitChip = show;
      this.showFilterByClusterChip = show;
      this.showFilterByChangeChip = show;
    },
  },
  created() {
    var udl = this.unfilteredData.length;
    if (udl > this.subsamplingMin) {
      if (udl >= this.subsamplingMin * 100) {
        this.subsample_choice = 0; // 1%
      } else if (udl >= this.subsamplingMin * 20) {
        this.subsample_choice = 1; // 5%
      } else if (udl >= this.subsamplingMin * 10) {
        this.subsample_choice = 2; // 10%
      } else if (udl >= this.subsamplingMin * 5) {
        this.subsample_choice = 3; // 20%
      } else {
        this.subsample_choice = 4; // 50%
      }
      this.subsample_data = true;
    }
  },
  mounted() {
    this.container = this.$refs.container;
    this.canvas = this.$refs.canvas;
    this.context = select(this.canvas)
      .node()
      .getContext('2d');

    // Resize chart so we have parent dimensions (width/height)
    this.resizeChart();
    this.mounted = true;

    this.$nextTick(() => this.updateCanvas());
  },
  methods: {
    ...mapActions('cohortManager', {
      addDimension: actions.ADD_DIMENSION,
      setInputVariables: actions.SET_INPUT_VARIABLES,
      setOutputVariables: actions.SET_OUTPUT_VARIABLES,
      addFilter: actions.ADD_FILTER,
      clearAllFilters: actions.CLEAR_ALL_FILTERS,
      saveSelectedCohort: actions.SAVE_SELECTED_COHORT,
      clearFilter: actions.CLEAR_FILTER,
    }),
    ...mapActions('notifications', {
      notifyError: notifyActions.NOTIFY_ERROR,
    }),
    resetClicked(type) {
      let dimension = this.variable.label + ' - ' + type;
      if (type == 'Last Visit') {
        this.tfRangeMin = null;
        this.tfRangeMax = null;
        this.selectedRange = null;
        this.selectedPopSubset = null;
      }
      if (type == this.cluster_choice) {
        this.resetAllClusterSelection();
      }
      this.clearFilter({ dimension: dimension });
      this.$emit('userResetVariable', dimension);
    },
    comparePredefs() {
      let ranges = this.$refs.last.getPredefinedRanges();
      this.$emit('comparePredefinedRanges', ranges);
    },
    savePredefs() {
      let ranges = this.$refs.last.getPredefinedRanges();
      this.$emit('savePredefinedRanges', ranges);
    },
    newCohort() {
      this.setNewCohort({ id: -1, label: 'New Cohort' });
    },
    userChangedVariable() {
      this.$emit('userChangedVariable');
    },
    resizeChart() {
      this.height = 300;
      this.width = 140;
    },
    subsample(old) {
      var sampled = [];
      var delta = [100, 20, 10, 5, 2][this.subsample_choice];
      if (delta == 1) return old;
      for (var i = 0; i < old.length; i += delta) {
        sampled.push(old[i]);
      }
      return sampled;
    },
    xDimensionScale(visit) {
      return visit == 'First Visit' ? 0 : this.computedWidth;
    },
    drawCurve({ firstVisitCoordinates, lastVisitCoordinates }) {
      const { context } = this;
      context.beginPath();
      context.moveTo(firstVisitCoordinates.x, firstVisitCoordinates.y);
      context.bezierCurveTo(
        firstVisitCoordinates.x +
          (lastVisitCoordinates.x - firstVisitCoordinates.x) / 4,
        firstVisitCoordinates.y,
        lastVisitCoordinates.x -
          (lastVisitCoordinates.x - firstVisitCoordinates.x) / 4,
        lastVisitCoordinates.y,
        lastVisitCoordinates.x,
        lastVisitCoordinates.y
      );
      context.stroke();
    },
    drawUnfiltered(paths, subjIdsD, color) {
      const { context } = this;
      let nCurvesDrawn = 0;
      context.lineWidth = 1;
      context.strokeStyle = color;
      context.globalAlpha = 1;
      paths.forEach(path => {
        if (!(path.subject_id in subjIdsD)) {
          this.drawCurve(path);
          ++nCurvesDrawn;
        }
      });
      return nCurvesDrawn;
    },
    drawFiltered(paths, subjIdsD) {
      const { context } = this;
      let nCurvesDrawn = 0;
      context.lineWidth = 1;
      context.globalAlpha = 0.45;
      context.strokeStyle = colors['cohort'];
      paths.forEach(path => {
        if (path.subject_id in subjIdsD) {
          this.drawCurve(path);
          ++nCurvesDrawn;
        }
      });
      return nCurvesDrawn;
    },
    drawPopulationClusters() {
      var pclust = this.getClusters();
      this._updateClusters(pclust);

      var popColor = colors['population'];
      var cohortColor = colors['cohort'];

      // interpolate color between population color and cohort color based
      // on fraction of cluster subjects in the cohort
      var pc = hexToRgbA(popColor);
      var cc = hexToRgbA(cohortColor);
      var pcd = [cc[0] - pc[0], cc[1] - pc[1], cc[2] - pc[2]];

      const { context } = this;
      context.globalAlpha = 1;

      var fv_x = this.xDimensionScale('First Visit');
      var lv_x = this.xDimensionScale('Last Visit');

      var ht = this.computedHeight - this.margin.bottom;
      var ns = this.unfilteredData.length;

      // filter and sort clusters - highlighted cluster last
      var dclust = pclust.filter(c => c.displayed);
      var sclust = dclust.sort((x, y) => {
        var xs = x.data.length - (x.highlight ? ns : 0);
        var ys = y.data.length - (y.highlight ? ns : 0);
        return ys - xs;
      });

      sclust.forEach(c => {
        var fv_y = this.dimensionScale(c['avg_f']);
        var lv_y = this.dimensionScale(c['avg_l']);

        // interpolated color
        var cf = c['cohort_frac'];
        var col = [
          pc[0] + cf * pcd[0],
          pc[1] + cf * pcd[1],
          pc[2] + cf * pcd[2],
        ];

        var ccol = 'rgb(' + col.join(',') + ',0.7)';
        if (c.highlight) {
          ccol = 'rgb(255,0,0,0.9)';
        }

        var bcurve = function(context, fv_x, fv_y, lv_x, lv_y) {
          context.bezierCurveTo(
            fv_x + (lv_x - fv_x) / 4,
            fv_y,
            lv_x - (lv_x - fv_x) / 4,
            lv_y,
            lv_x,
            lv_y
          );
        };

        var fv_ymin, fv_ymax, lv_ymin, lv_ymax;

        // set line width based on number of subjects in cluster
        var ns_lwidth = (c['data'].length / ns) * ht * 0.1;
        if (ns_lwidth < 1) ns_lwidth = 1;

        // OPTION 1: draw line around average with thickness determined by number of subjects
        if (this.cluster_style_choice == this.cluster_style_choices[0]) {
          context.beginPath();
          context.moveTo(fv_x, fv_y);
          bcurve(context, fv_x, fv_y, lv_x, lv_y);
          context.lineWidth = ns_lwidth;
          context.strokeStyle = ccol;
          context.stroke();
        }
        // OPTION 2: draw average line +/- 1 SD
        else if (this.cluster_style_choice == this.cluster_style_choices[1]) {
          // +1 SD
          fv_ymax = this.dimensionScale(c['avg_f'] + c['deviation_f']);
          lv_ymax = this.dimensionScale(c['avg_l'] + c['deviation_l']);

          // -1 SD
          fv_ymin = this.dimensionScale(c['avg_f'] - c['deviation_f']);
          lv_ymin = this.dimensionScale(c['avg_l'] - c['deviation_l']);

          context.lineWidth = 1;

          // fill area from +1 SD to -1 SD
          context.beginPath();
          context.moveTo(fv_x, fv_ymax);
          bcurve(context, fv_x, fv_ymax, lv_x, lv_ymax);
          context.lineTo(lv_x, lv_ymin);
          bcurve(context, lv_x, lv_ymin, fv_x, fv_ymin);
          context.lineTo(fv_x, fv_ymax);
          context.fillStyle = c.highlight
            ? 'rgb(208,208,208,0.6)'
            : 'rgb(208,208,208,0.3)';
          context.fill();

          context.strokeStyle = ccol;

          // +1 SD
          context.beginPath();
          context.moveTo(fv_x, fv_ymax);
          bcurve(context, fv_x, fv_ymax, lv_x, lv_ymax);
          context.stroke();

          // -1 SD
          context.beginPath();
          context.moveTo(fv_x, fv_ymin);
          bcurve(context, fv_x, fv_ymin, lv_x, lv_ymin);
          context.stroke();

          // average, width = num clusters
          context.lineWidth = ns_lwidth;
          context.beginPath();
          context.moveTo(fv_x, fv_y);
          bcurve(context, fv_x, fv_y, lv_x, lv_y);
          context.stroke();
        }
        // OPTION 3: draw average line plus entire area around min/max
        else if (this.cluster_style_choice == this.cluster_style_choices[2]) {
          // max
          fv_ymax = this.dimensionScale(c['max_f']);
          lv_ymax = this.dimensionScale(c['max_l']);

          // min
          fv_ymin = this.dimensionScale(c['min_f']);
          lv_ymin = this.dimensionScale(c['min_l']);

          context.beginPath();
          context.strokeStyle = '#000';

          context.moveTo(fv_x, fv_ymin);
          context.lineWidth = 0;
          context.lineTo(fv_x, fv_ymax);
          context.lineWidth = 1;
          context.bezierCurveTo(
            fv_x + (lv_x - fv_x) / 4,
            fv_ymax,
            lv_x - (lv_x - fv_x) / 4,
            lv_ymax,
            lv_x,
            lv_ymax
          );

          context.lineWidth = 0;
          context.lineTo(lv_x, lv_ymin);
          context.lineWidth = 1;
          context.bezierCurveTo(
            lv_x - (lv_x - fv_x) / 4,
            lv_ymin,
            fv_x + (lv_x - fv_x) / 4,
            fv_ymin,
            fv_x,
            fv_ymin
          );

          context.fillStyle = 'rgb(208,208,208,0.3)';
          context.fill();

          // trace outlines
          context.strokeStyle = ccol;
          context.beginPath();
          context.moveTo(fv_x, fv_ymax);
          context.bezierCurveTo(
            fv_x + (lv_x - fv_x) / 4,
            fv_ymax,
            lv_x - (lv_x - fv_x) / 4,
            lv_ymax,
            lv_x,
            lv_ymax
          );
          context.moveTo(lv_x, lv_ymin);
          context.bezierCurveTo(
            lv_x - (lv_x - fv_x) / 4,
            lv_ymin,
            fv_x + (lv_x - fv_x) / 4,
            fv_ymin,
            fv_x,
            fv_ymin
          );
          context.stroke();

          // average, width = num clusters
          context.lineWidth = ns_lwidth;
          context.beginPath();
          context.moveTo(fv_x, fv_y);
          bcurve(context, fv_x, fv_y, lv_x, lv_y);
          context.stroke();
        }
      });
    },
    updateCanvas() {
      if (!this.mounted) return;
      this.context.clearRect(0, 0, this.width, this.height);
      // individual subjects
      if (this.show_raw_data) {
        //        let t1 = Date.now();
        let allPaths = this.populationPaths;
        // subjects in current cohort
        let cohortSubjIds = {};
        this.filteredData.map(d => {
          cohortSubjIds[d.subject_id] = 1;
        });
        this.drawUnfiltered(allPaths, cohortSubjIds, colors['population']);
        this.drawFiltered(allPaths, cohortSubjIds);
      }
      // clusters of subjects
      if (this.draw_clusters) {
        this.drawPopulationClusters();
      }

      // draw selected range
      if (this.tfRangeMin == null || this.tfRangeMax == null) {
        return;
      }
      var tfMin = Number(this.tfRangeMin);
      var tfMax = Number(this.tfRangeMax);

      if (tfMax > this.padded_max_value) {
        return;
      }
      if (tfMin < this.padded_min_value) {
        return;
      }
    },
    _addToCluster(cluster, d) {
      cluster['data'].push(d);

      if (!('sum_f' in cluster)) cluster['sum_f'] = 0;
      if (!('sum_l' in cluster)) cluster['sum_l'] = 0;

      var f = d[this.dimensionName]['firstVisit'];
      var l = d[this.dimensionName]['lastVisit'];

      if (!('min_f' in cluster) || f < cluster['min_f']) cluster['min_f'] = f;
      if (!('max_f' in cluster) || f > cluster['max_f']) cluster['max_f'] = f;
      cluster['sum_f'] += d[this.dimensionName]['firstVisit'];

      if (!('min_l' in cluster) || l < cluster['min_l']) cluster['min_l'] = l;
      if (!('max_l' in cluster) || l > cluster['max_l']) cluster['max_l'] = l;
      cluster['sum_l'] += d[this.dimensionName]['lastVisit'];

      if (!('selected' in cluster)) cluster['selected'] = false;
      if (cluster['data'].length > 20) cluster['selected'] = true;
    },
    _initClusters(clusters) {
      var dname = this.dimensionName;

      clusters.forEach(c => {
        c['avg_f'] = c['sum_f'] / c['data'].length;
        c['avg_l'] = c['sum_l'] / c['data'].length;

        var fv_values = c['data'].map(d => d[dname]['firstVisit']);
        var lv_values = c['data'].map(d => d[dname]['lastVisit']);

        c['deviation_f'] = deviation(fv_values);
        c['deviation_l'] = deviation(lv_values);
      });
    },
    _updateClusters(clusters) {
      var fd = this.filteredData;

      // index subjects in filteredData
      var fsh = {};
      fd.forEach(d => {
        fsh[d['subject_id']] = true;
      });

      clusters.forEach(c => {
        c['num_in_cohort'] = 0;
        c['data'].forEach(d => {
          if (d['subject_id'] in fsh) {
            c['num_in_cohort'] += 1;
          }
        });
        c['cohort_frac'] = c['num_in_cohort'] / c['data'].length;
      });
    },
    getClusters() {
      var clusters = null;

      // check cache
      var ckey =
        this.cluster_choice +
        ':' +
        this.cluster_step_choice +
        ':' +
        this.dimensionName;
      if (!(ckey in this.clusters_cache)) {
        clusters = this.computeClusters(this.unfilteredData);
        this._initClusters(clusters);
        this.clusters_cache[ckey] = clusters;
      }

      clusters = this.clusters_cache[ckey];
      this.clusters = clusters;
      this.num_clusters = clusters.length;
      var n_displayed = 0;

      // filter by length
      clusters.forEach(c => {
        c.displayed = c.data.length >= this.min_cluster_size;
        if (c.displayed) n_displayed += 1;
      });

      this.num_clusters_displayed = n_displayed;
      return clusters;
    },
    computeClusters(data) {
      var dname = this.dimensionName;
      let stepSize = this.cluster_step_choice;
      var val_fn = null;

      // calculate standard deviation
      if (this.cluster_choice == 'Change') {
        val_fn = function(d) {
          return d[dname]['change'];
        };
      } else if (this.cluster_choice == 'Last Visit') {
        val_fn = function(d) {
          return d[dname]['lastVisit'];
        };
      }

      var values = data.map(d => val_fn(d)).filter(n => isFinite(n));
      var mn = mean(values);
      var dev = deviation(values);
      var vmin = min(values);
      var vmax = max(values);

      // create a list of step thresholds based on stepSize ( this part has to be hard coded )
      // 0.5 SD -> 9 steps | 1 SD -> 5 steps | 1.5 SD -> 5 steps
      let stepThresholdList = [];
      let numericThresholdList = [];
      if (stepSize == '0.25 SD') {
        stepThresholdList = [
          -2,
          -1.75,
          -1.5,
          -1.25,
          -1,
          -0.75,
          -0.5,
          -0.25,
          0.25,
          0.5,
          0.75,
          1,
          1.25,
          1.5,
          1.75,
          2,
        ];
      } else if (stepSize == '0.5 SD') {
        stepThresholdList = [-2, -1.5, -1, -0.5, 0.5, 1, 1.5, 2];
      } else if (stepSize == '1 SD') {
        stepThresholdList = [-2, -1, 1, 2];
      } else if (stepSize == '1.5 SD') {
        stepThresholdList = [-3, -1.5, 1.5, 3];
      }

      for (let i = 0; i < stepThresholdList.length; i++) {
        numericThresholdList[i] = stepThresholdList[i] * dev + mn;
      }
      numericThresholdList.push(vmax);

      // create a list of cluster objects
      let newClusterList = [];
      for (let i = 0; i < stepThresholdList.length; i++) {
        if (i == 0) {
          let clusterObj = {
            label: '< ' + stepThresholdList[i],
            data: [],
          };
          newClusterList.push(clusterObj);
        }
        if (i == stepThresholdList.length - 1) {
          let clusterObj = {
            label: '> ' + stepThresholdList[i],
            data: [],
          };
          newClusterList.push(clusterObj);
        }
        if (i != stepThresholdList.length - 1) {
          let clusterObj = {
            label: stepThresholdList[i] + ' to ' + stepThresholdList[i + 1],
            data: [],
          };
          newClusterList.push(clusterObj);
        }
      }
      // assign each subject to a cluster
      data.forEach(d => {
        var val = val_fn(d);
        for (let i = 0; i < numericThresholdList.length; i++) {
          if (val <= numericThresholdList[i]) {
            this._addToCluster(newClusterList[i], d);
            break;
          }
        }
      });

      let clusterTitle =
        this.cluster_choice +
        ' ' +
        this.cluster_step_choice +
        ' ' +
        this.variable.label;
      // store clusterThreshold and clusterMax and clusterMin in clusterThreshold array
      let clusterThresholdObj = {};

      clusterThresholdObj[clusterTitle] = {
        clusterThreshold: numericThresholdList,
        clusterMax: vmax,
        clusterMin: vmin,
      };
      this.clusterThreshold.push(clusterThresholdObj);

      return newClusterList;
    },
    subsampleClicked(e) {
      e.cancelBubble = true;
    },
    clusterHover(c) {
      c.highlight = true;
      this.$nextTick(() => this.updateCanvas());
    },
    clusterUnhover(c) {
      c.highlight = false;
      this.$nextTick(() => this.updateCanvas());
    },
    computeClusterMinMax(c) {
      // find the index of the caller element
      let index = this.clusters.findIndex(cluster => cluster === c);
      let currentNumberOfClusters = this.clusters.length;
      let currentClusterSelected = this.cluster_choice;
      var min = -1;
      var max = -1;

      let tempClusterThreshold = {};
      // loop through clusterThreshold to find the current clusterThreshold
      this.clusterThreshold.forEach(c => {
        if (
          Object.keys(c)[0] ==
          currentClusterSelected +
            ' ' +
            this.cluster_step_choice +
            ' ' +
            this.variable.label
        ) {
          tempClusterThreshold = c[Object.keys(c)[0]];
        }
      });

      // find corresponding min and max based on index
      if (index == 0) {
        min = tempClusterThreshold.clusterMin - 0.01;
        max = tempClusterThreshold.clusterThreshold[0];
      } else if (index == currentNumberOfClusters - 1) {
        min =
          tempClusterThreshold.clusterThreshold[currentNumberOfClusters - 2];
        max = tempClusterThreshold.clusterMax + 0.01;
      } else {
        min = tempClusterThreshold.clusterThreshold[index - 1];
        max = tempClusterThreshold.clusterThreshold[index];
      }

      return [min, max];
    },
    clusterClick(c) {
      // clusters with no data
      if (c.data.length == 0) {
        return;
      }

      // check if c is already selected, if yes, then set clickedCluster to null
      if (this.clickedCluster == c) {
        this.clickedCluster = null;
        this.resetClicked('Last Visit');
        return;
      }

      this.clusterErrorMessage = '';

      let fullDimensionTitle =
        this.variable.label + ' - ' + this.cluster_choice;
      this.clickedCluster = c;

      const [min, max] = this.computeClusterMinMax(c);

      const newFilter = {
        dimension: fullDimensionTitle,
        filter: d => d >= min && d < max,
        query: {
          minValue: min,
          maxValue: max,
        },
      };

      this.addFilter(newFilter);
      this.$emit('userChangedVariable', this.dimensionName);
    },
    resetAllClusterSelection() {
      this.clickedCluster = null;
    },
    userChangedFilter(data) {
      this.tfRangeMin = data.query.minValue;
      this.tfRangeMax = data.query.maxValue;
    },
    resetFilter() {
      this.tfRangeMin = null;
      this.tfRangeMax = null;
      this.selectedRange = null;
      this.selectedPopSubset = null;
    },
    updateRangeFromQuery(
      manualMin,
      manualMax,
      selectedRange,
      selectedPopSubset
    ) {
      this.tfRangeMin = manualMin;
      this.tfRangeMax = manualMax;
      this.selectedRange = selectedRange;
      this.selectedPopSubset = selectedPopSubset;
    },
    async saveCluster(label) {
      if (this.filteredData.length == 0) {
        this.clusterErrorMessage = 'Cluster size is 0';
      } else {
        this.clusterErrorMessage = '';

        // clusterName = variableName(e.g. Benton JOLO) +  clusterName(e.g. Last Visit 5) + clusterStyle(e.g. +/- 1 SD)
        let clusterName =
          '[O]_' +
          this.variable.abbreviation +
          ' ' +
          this.cluster_choice +
          ' ' +
          label +
          ' ' +
          'SD';

        // check if the cluster name already exist, if yes then set clusterNameExist to true
        this.cohorts.forEach(c => {
          if (c.label === clusterName) {
            this.clusterErrorMessage = 'Study group already exists';
          }
        });

        // check if input variables are selected
        if (this.outputVariables.length == 0) {
          this.clusterErrorMessage = 'Predictor variables not yet selected';
        }

        // save cluster
        if (this.clusterErrorMessage == '') {
          await this.saveSelectedCohort({ cohortName: clusterName });
        }
      }

      // clear cluster and all filters
      this.resetAllClusterSelection();
      this.clearAllFilters();
    },
    async saveClusters() {
      // reset currently clicked cluster
      this.clickedCluster = null;
      // save all clusters currently displayed, except for cluster with size 0
      let oneOrMoreClusterExist = false;
      let oneOrMoreClusterSizeZero = false;
      // iterate through clusters, call clusterClick for each cluster
      // after each addFilter request, call saveSelectedCohort
      for (const c of this.clusters) {
        if (c.selected) {
          this.clusterClick(c);

          await this.saveCluster(c.label);
          if (this.clusterErrorMessage == 'Group name already exist') {
            oneOrMoreClusterExist = true;
          }
          if (this.clusterErrorMessage == 'Group size is 0') {
            oneOrMoreClusterSizeZero = true;
          }
        }
      }

      if (oneOrMoreClusterExist) {
        this.clusterErrorMessage = 'Group name already exists';
      }
      if (oneOrMoreClusterSizeZero) {
        this.clusterErrorMessage = 'Group(s) of size 0 is not saved';
      }
      if (oneOrMoreClusterExist && oneOrMoreClusterSizeZero) {
        this.clusterErrorMessage =
          'Group name already exists and group(s) of size 0 is not saved';
      }

      if (this.clusterErrorMessage != '') {
        this.notifyError(this.clusterErrorMessage);
      }
      // reset clickedCluster to null
      this.clickedCluster = null;
    },
    async mergeAndSaveClusters() {
      // reset currently clicked cluster
      this.clickedCluster = null;
      this.clusterErrorMessage = '';

      let ranges = [];
      let labels = [];

      // assuming all clusters are pre-sorted
      for (const c of this.clusters) {
        if (c.selected) {
          ranges.push(this.computeClusterMinMax(c));
          labels.push(c.label.split(' to '));
        }
      }

      let mergedMin = ranges[0][0];
      let mergedMax = ranges[0][1];

      for (let i = 1; i < ranges.length; i++) {
        const [min, max] = ranges[i];

        if (min <= mergedMax + 0.01) {
          mergedMax = Math.max(mergedMax, max);
        } else {
          this.clusterErrorMessage = 'Selected groups are not contiguous';
        }
      }

      if (this.clusterErrorMessage == '') {
        let fullDimensionTitle =
          this.variable.label + ' - ' + this.cluster_choice;

        const newFilter = {
          dimension: fullDimensionTitle,
          filter: d => d >= mergedMin && d < mergedMax,
          query: {
            minValue: mergedMin,
            maxValue: mergedMax,
          },
        };

        this.addFilter(newFilter);
        this.$emit('userChangedVariable', this.dimensionName);

        let minLabel = labels[0][0];
        let temp = labels[labels.length - 1];
        let maxLabel = temp[temp.length - 1];
        let label = minLabel + ' to ' + maxLabel;

        await this.saveCluster(label);
      } else {
        this.notifyError(this.clusterErrorMessage);
      }
    },
    selectAllClusters() {
      if (this.allClustersSelected) {
        for (const c of this.clusters) {
          if (c.data.length > 0) {
            c.selected = true;
          }
        }
      } else {
        for (const c of this.clusters) {
          c.selected = false;
        }
      }
    },
    comparePredefinedRanges(ranges) {
      this.$emit('comparePredefinedRanges', ranges);
    },
    savePredefinedRanges(ranges) {
      this.$emit('savePredefinedRanges', ranges);
    },
  },
};
</script>

<style lang="scss" scoped>
.title-card {
  width: calc(100% + 12px);
  margin-bottom: 1rem;
}

.panel-col {
  display: flex;
  flex-direction: column;
  align-content: center;
  align-items: center;
  border-top: 1px solid lightgray;
}

.right-border {
  border-right: 1px solid lightgray;
}

.highlighted-row {
  background-color: #e0e0e0;
  /* or any other highlight color */
}
</style>
